/* Feb-2026 · useCostPreflight — reusable wrapper hook for AI actions.
 *
 * Wraps any async "fire an AI job" function so callers get a pre-flight
 * cost dialog for free. Pattern:
 *
 *   const { PreflightDialog, runWithPreflight } = useCostPreflight({
 *     model, words, remainingUsd, action: 'draft this chapter',
 *   });
 *
 *   <PreflightDialog />
 *   <Button onClick={() => runWithPreflight(() => generateChapter(id))}>
 *     Draft chapter
 *   </Button>
 *
 * If the user has ticked "Don't ask again under $X" on a previous run
 * and this estimate is under the threshold, the wrapper skips the
 * dialog and invokes the action directly. Otherwise it opens the
 * dialog and only fires the action when the user clicks Approve.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import CostPreflightDialog, {
  shouldSkipPreflight,
} from '@/components/dialogs/CostPreflightDialog';
import { estimatePriceUsd } from '@/lib/cost';

export default function useCostPreflight({
  model, words, remainingUsd,
  action = 'run this',
}) {
  const [open, setOpen] = useState(false);
  const pendingFn = useRef(null);

  const estimatedUsd = useMemo(
    () => (model && words ? estimatePriceUsd(words, model).final_price_usd : 0),
    [model, words],
  );

  const runWithPreflight = useCallback((fn) => {
    if (!fn) return;
    // No model info yet? Fall through to the raw action — better than
    // blocking generation on a pricing race condition.
    if (!model || !words) { fn(); return; }
    if (shouldSkipPreflight(estimatedUsd)) { fn(); return; }
    pendingFn.current = fn;
    setOpen(true);
  }, [model, words, estimatedUsd]);

  const onConfirm = useCallback(() => {
    try { pendingFn.current?.(); }
    finally { pendingFn.current = null; }
  }, []);

  const onCancel = useCallback(() => {
    pendingFn.current = null;
  }, []);

  const PreflightDialog = useCallback(() => (
    <CostPreflightDialog
      open={open}
      onOpenChange={setOpen}
      words={words}
      model={model}
      action={action}
      remainingUsd={remainingUsd}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  ), [open, words, model, action, remainingUsd, onConfirm, onCancel]);

  return { PreflightDialog, runWithPreflight, estimatedUsd };
}
